'use client'

import { useEffect, useRef } from 'react'
import { useTranslations } from 'next-intl'
import { Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { isPlausibleLoginCode, LOGIN_CODE_MAX_LENGTH } from '@/lib/auth/login-code'
import type { RequestLoginCodeAction, VerifyLoginCodeAction } from '@/lib/auth/login-code'
import { useLoginCode, type LoginCodeError } from '@/lib/auth/use-login-code'

const ERROR_KEY: Record<LoginCodeError, string> = {
  invalid_email: 'errors.invalidEmail',
  invalid_code: 'errors.invalidCode',
  rate_limited: 'errors.rateLimited',
  failed: 'errors.failed',
  profile_error: 'errors.profileError',
}

/** Text links of this panel: 44 px target, underlined, visible keyboard focus. */
const LINK =
  'inline-flex min-h-11 items-center rounded-sm underline underline-offset-4 hover:no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-ring'

/**
 * "Entrar con un código por mail" of the classic login (session audit R4):
 * address, then the code from the mail. The mail also carries a link, which
 * works on its own; this screen is for typing or autofilling the code.
 */
export function LoginCodePanel({
  request,
  verify,
  next,
  initialEmail,
  onBack,
}: {
  request: RequestLoginCodeAction
  verify: VerifyLoginCodeAction
  next?: string | null
  initialEmail?: string
  onBack: () => void
}) {
  const t = useTranslations('auth.loginCode')
  const flow = useLoginCode({ request, verify, next, initialEmail })
  // A wrong code must not say "ask for a new one" while the resend is still
  // cooling down: say how long is left instead.
  const errorKey =
    flow.error === 'invalid_code' && flow.cooldown > 0
      ? 'errors.invalidCodeWait'
      : flow.error
        ? ERROR_KEY[flow.error]
        : null
  const errorText = errorKey ? t(errorKey as never, { seconds: flow.cooldown } as never) : null

  // After a failure the focus goes back to the field, with its content
  // selected, so the next attempt is typing over it.
  const emailRef = useRef<HTMLInputElement>(null)
  const codeRef = useRef<HTMLInputElement>(null)
  const errorSeen = useRef<string | null>(null)
  // Another code on request: the field is where the person goes next.
  const resend = async () => {
    if (await flow.send()) codeRef.current?.focus()
  }
  useEffect(() => {
    if (!flow.error || flow.busy) {
      if (!flow.error) errorSeen.current = null
      return
    }
    if (errorSeen.current === flow.error) return
    errorSeen.current = flow.error
    const target = flow.step === 'code' ? codeRef.current : emailRef.current
    target?.focus()
    if (flow.step === 'code') target?.select()
  }, [flow.error, flow.busy, flow.step])

  return (
    <div className="space-y-5 md:space-y-4 mt-4" data-testid="login-code-panel">
      <div className="text-center">
        <h2 className="font-serif text-xl font-bold text-primary">{t('title')}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t('subtitle')}</p>
      </div>

      {flow.step === 'email' ? (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (!flow.busy) void flow.send()
          }}
        >
          <div>
            <label htmlFor="login-code-email" className="block text-sm font-medium mb-1.5 md:mb-1">
              {t('emailLabel')}
            </label>
            <Input
              id="login-code-email"
              name="email"
              type="email"
              value={flow.email}
              onChange={(e) => flow.setEmail(e.target.value)}
              autoComplete="email"
              inputMode="email"
              enterKeyHint="send"
              required
              ref={emailRef}
              readOnly={flow.busy}
              aria-busy={flow.busy || undefined}
              aria-invalid={flow.error === 'invalid_email' || undefined}
              className="bg-white md:bg-[#fdfbf7] h-12 md:h-10 text-base md:text-sm"
            />
          </div>
          {errorText && (
            <p role="alert" className="text-sm text-red-700">
              {errorText}
            </p>
          )}
          <Button
            type="submit"
            disabled={flow.cooldown > 0 || !flow.email}
            aria-disabled={flow.busy || undefined}
            className="w-full font-bold h-12 md:h-10 aria-disabled:opacity-50"
          >
            {flow.busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {t('sending')}
              </>
            ) : (
              t('send')
            )}
          </Button>
        </form>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (!flow.busy) void flow.submitCode()
          }}
        >
          <p className="text-sm text-muted-foreground" role="status">
            {t('sent', { email: flow.email })}
          </p>
          <div>
            <label htmlFor="login-code" className="block text-sm font-medium mb-1.5 md:mb-1">
              {t('codeLabel')}
            </label>
            <Input
              id="login-code"
              name="code"
              type="text"
              value={flow.code}
              onChange={(e) => flow.setCode(e.target.value)}
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={LOGIN_CODE_MAX_LENGTH}
              enterKeyHint="done"
              autoFocus
              required
              ref={codeRef}
              readOnly={flow.busy}
              aria-busy={flow.busy || undefined}
              aria-invalid={flow.error === 'invalid_code' || undefined}
              className="bg-white md:bg-[#fdfbf7] h-12 md:h-10 text-center text-xl tracking-[0.3em]"
            />
            <p className="text-xs text-muted-foreground mt-1">{t('codeHelp')}</p>
          </div>
          {errorText && (
            <p role="alert" className="text-sm text-red-700">
              {errorText}
            </p>
          )}
          <Button
            type="submit"
            disabled={!isPlausibleLoginCode(flow.code)}
            aria-disabled={flow.busy || undefined}
            className="w-full font-bold h-12 md:h-10 aria-disabled:opacity-50"
          >
            {flow.busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {t('verifying')}
              </>
            ) : (
              t('verify')
            )}
          </Button>
          <div className="flex flex-col items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => void resend()}
              disabled={flow.busy || flow.cooldown > 0}
              className={`${LINK} text-primary font-semibold disabled:text-muted-foreground disabled:no-underline`}
            >
              {flow.cooldown > 0 ? t('resendIn', { seconds: flow.cooldown }) : t('resend')}
            </button>
            <button
              type="button"
              onClick={flow.changeEmail}
              className={`${LINK} text-muted-foreground`}
            >
              {t('changeEmail')}
            </button>
          </div>
        </form>
      )}

      <div className="text-center">
        <button
          type="button"
          onClick={onBack}
          className={`${LINK} text-sm text-muted-foreground hover:text-primary`}
        >
          {t('back')}
        </button>
      </div>
    </div>
  )
}
